import request from '../../../util/SuperagentUtils'
import { accountEditEndpoints, userRequests } from '../../../requests/api-endpoints';
import {Account} from "../../../types/Account";
import {AccountUser} from "../../../types/AccountUser";
import {FundingSourceAccount} from "../../../types/FundingSourceAccount";

export interface AccountUpdateRequest {
    id: string;
    firstName: string;
    lastName: string;
    companyName: string;
    companyDescription?: string | null;
    email: string;
    username: string;
    phoneNumber?: string;
    addressLine1?: string;
    addressLine2?: string;
    addressLatitude?: string;
    addressLongitude?: string;
    city?: string;
    state?: string;
    zip?: string;
    // True only when the user explicitly chose to keep their own typed
    // address text over Google's suggestion in the address-confirmation
    // dialog on the Company Details form. False for dropdown-selected or
    // silently auto-matched addresses. Matches the `addressUserAsserted`
    // field on the backend's `AccountUpdateVO`.
    addressUserAsserted?: boolean;
    listingImageFileName?: string;
    supplierLegalAgreementFileName?: string;
    payoutSchedule?: string;
    type: string;
    useCalendarDays: boolean;
}

export interface AdminAccountUpdateRequest {
    feePercentage: number;
    billingFeePercentage: number;
    securspaceServiceFeePercentage: number;
    subscriptionType?: string;
    subscriptionFee?: number;
    subscriptionEffectiveDate?: string;
    activated: boolean;
}

export const updateAccount = (account: AccountUpdateRequest) => {
    return request.put(accountEditEndpoints.account.update).send(account);
};

export const updateAccountAdmin = (accountId: string, body: AdminAccountUpdateRequest) => {
    return request.put(accountEditEndpoints.admin.update(accountId)).send(body);
};

export const getAllBuyerAccounts = () => {
    return request.get(userRequests.admin.getAllBuyers);
};

export const getBuyersForSupplier = () => {
    return request.get(userRequests.supplier.getBuyersForSupplier);
};

export const confirmTermsOfService = (accountId: string, account: Account) => {
    return request.post(accountEditEndpoints.conformToS(accountId)).send(account);
}

export interface PaginationParams {
    page: number;
    size: number;
}

export interface PageVO<T> {
    content: T[];
    page: number;
    rowsPerPage: number;
    count: number;
    totalPages: number;
}

/**
 * Query params accepted by the paginated account-users endpoint.
 * All filter/sort fields are optional; only populated values are sent.
 */
export interface AccountUserQueryParams extends PaginationParams {
    /** Case-insensitive match against username, first name, last name, or full name. */
    searchTerm?: string;
    /** DB role values to filter by (e.g. ROLE_USERTYPE_OWNER). Repeated as roles=A&roles=B. */
    roles?: string[];
    /** Sort column key: NAME | USERNAME | ROLE. */
    sortBy?: string;
    /** Sort direction. */
    sortDir?: 'asc' | 'desc';
}

/**
 * Get users for an account (paginated, with optional search / role filter / sort).
 * @param accountId - The account ID
 * @param params - Page/size plus optional searchTerm, roles, sortBy, sortDir
 */
export const getAccountUsers = (accountId: string, params: AccountUserQueryParams = { page: 0, size: 10 }) => {
    const { page, size, searchTerm, roles, sortBy, sortDir } = params;

    const query: Record<string, string | number | string[]> = { page, size };
    if (searchTerm && searchTerm.trim()) {
        query.searchTerm = searchTerm.trim();
    }
    if (roles && roles.length > 0) {
        query.roles = roles;
    }
    if (sortBy) {
        query.sortBy = sortBy;
    }
    if (sortDir) {
        query.sortDir = sortDir;
    }

    return request
        .get(accountEditEndpoints.accountUser.get(accountId))
        .query(query);
};

export const createNewUser = (user: AccountUser) => {
    return request.post(accountEditEndpoints.accountUser.create).send(user);
};

export const updateExistingUser = (user: AccountUser) => {
    return request.put(accountEditEndpoints.accountUser.update).send(user);
}

export const deleteUser = (user: AccountUser) => {
    return request.delete(accountEditEndpoints.accountUser.remove).send(user);
};

/**
 * Request a Cognito token for a user
 * @param accountId - The account ID
 * @param username - The username
 */
export const requestCognitoTokenForUser = (
    accountId: string,
    username: string) => {

    if (accountId && username) {
        return request
            .get(accountEditEndpoints.cognitoToken(accountId))
            .query({ email: username })
    } else {
        console.error("Attempting request for cognito token with invalid account id or username")
    }
}

/**
 * Create a funding source for an account
 * @param account - The account object containing funding source details
 */
export const createAccountFundingSource = (account: FundingSourceAccount) => {
    return request.post(accountEditEndpoints.fundingSources.create).send(account);
};

/**
 * Remove a funding source from an account
 * @param accountId - The account ID
 * @param onSuccess - Success callback
 * @param onError - Error callback
 */
export const removeAccountFundingSource = async (
  accountId: string,
  onSuccess: (body: any) => void,
  onError: (err: any) => void
) => {
  try {
    const resp = await request.del(accountEditEndpoints.fundingSources.remove(accountId));
    return onSuccess(resp.body);
  } catch (err_1) {
    return onError(err_1);
  }
}

/**
 * Update the locations a user has access to
 * @param accountId - The account ID
 * @param username - The username
 * @param locationIds - The location IDs
 */
export const requestUpdateUserAccessibleLocations = (accountId: string, username: string, locationIds: string[]) => {
    return request
        .put(accountEditEndpoints.updateUserAccessibleLocations(accountId))
        .send({ username, locationIds })
}

export const requestEmailVerificationToken = () => {
    return request.post(accountEditEndpoints.verificationEmail);
};
